// This js fetches item data from the /v1/items API and parses it to render in CourseOffers

import addStartupScript from 'mex_components/app/scripts/events/addStartupScript';
import weekNumber from 'mex_components/app/scripts/date/weekNumber';
import getMeta from 'mex_components/app/scripts/storage/getMeta';
import ClassTimes from './class_times';
import formats from './date/format';
import { userSubscriptions } from './item_buy_box';
import { classSummaries, condensedClassSummaries, convertTime, detailsSetUp }
 from './buybox_utils';
import * as offerDetails from './offer_details';

/**
 * Returns true if the subscription is for clubs
 *
 * @param {String} subscriptionProgramId Program id of the optional_subscription
 */
export const clubsSubscription = (subscriptionProgramId) => {
  return subscriptionProgramId === 'efecf605-89c6-4471-a678-1241468f3163';
};

/**
 * Returns true if the subscription is for VT Unlimited (clubs)
 *
 * @param {String} subscriptionProgramId Program id of the optional_subscription
 */
 export const vtUnlimitedSubscription = (subscriptionProgramId) => {
  return subscriptionProgramId === '3a294eb4-7754-48dd-9908-e14f9180c2d2';
};

const _checkoutLink = (offerListing, optionalSubscription) => {
  if (_courseIncluded(optionalSubscription)) {
    return `https://www.varsitytutors.com/payments/buynow_terminal/index.html?catalogItemId=${offerListing.item_id}`;
  } else {
    return offerListing.checkout_link;
  }
};

/**
 * Converts daytimes into a condensed array of unique times, where we can access
 * formatted time range, day range, start time, etc. in the user's browser time
 *
 * @param {Array} daytimes Array of start/end times
 * eg. {start_time: "2021-06-07T16:30:00Z", end_time: "2021-06-07T18:30:00Z"}
 */
const _condensedWeeklyDayTimes = (daytimes) => {
  if (!daytimes) return;
  return new ClassTimes(daytimes).condensedWeekly();
};

/**
 * Returns button text course offer
 *
 * @param {String} checkoutLinkType 'enroll' or 'subscribe_and_enroll'
 *
 * @returns 'Enroll' or 'Join & Enroll'
 */
const _enrollText = (checkoutLinkType, optionalSubscription) => {
  if (checkoutLinkType === 'subscribe_and_enroll') {
    if (_courseIncluded(optionalSubscription)) {
      return 'Enroll';
    }

    return 'Join & Enroll';
  }

  return 'Enroll';
};

/**
 * GET request to fetch a single item
 *
 * @param {String} fetchUrl Full API URL to GET
 * @param {String} itemId ID of the parent item
 *
 * @returns Raw API response
 */
const _fetch = async (fetchUrl) => {
  const response = await fetch(fetchUrl, {
    credentials: 'include'
  });
  const json = await response.json();
  return json;
};

/**
 * Converts item daytimes into a hyphenated range of days in the user's browser time
 *
 * @param {Array} daytimes Array of start/end times
 * eg. {start_time: "2021-06-07T16:30:00Z", end_time: "2021-06-07T18:30:00Z"}
 *
 * @returns Pretty string of day ranges, eg. 'Mon-Tue, Thu'
 */
const _formattedDays = (daytimes) => {
  const formattedDayTimes = _condensedWeeklyDayTimes(daytimes);
  if (!formattedDayTimes) return;
  return formattedDayTimes[0].daysRange;
};

/**
 * Converts item date string into a formatted date, eg. `Mon, Jun 7`
 *
 * @param {String} date Date string, eg. "2021-07-19T20:00:00Z/PT1H"
 * @param {Integer} offset Offset to add to the date in minutes
 *   (eg. if passing start time, need to add duration to calculate end time)
 *
 * @returns Formatted date (eg. 'Mon, Jun 7')
 */
const _formattedDate = (date, offset = 0) => {
  return formats.shortDate(new Date(convertTime(date, offset)));
};

/**
 * Parses array of meeting times and returns the first one after current time
 *
 * @param {Array} meetings Array of sorted date strings eg. [2021-07-19T20:00:00Z/PT1H, ...]
 * @param {Integer} classDuration Length of the class in minutes
 *   (eg. if passing start time, need to add duration to calculate end time)
 *
 * @returns Formatted date (eg. 'Mon, Jun 7') of next session after current time.
 */
const _nextSessionTime = (meetings, classDuration) => {
  if (!meetings) return;
  const currentTime = Date.now();
  const nextMeeting = meetings.find(
    (meeting) => convertTime(meeting.time, classDuration) >= currentTime
  );
  return nextMeeting?.time;
};

/**
 * Converts item daytimes into a time range in the user's browser time
 *
 * @param {Array} daytimes Array of start/end times
 * eg. {start_time: "2021-06-07T16:30:00Z", end_time: "2021-06-07T18:30:00Z"}
 *
 * @returns Condensed weekly times (eg. '9:30 AM - 11:30 AM (PT)')
 */
const _formattedTimes = (daytimes) => {
  const formattedDayTimes = _condensedWeeklyDayTimes(daytimes);
  if (!formattedDayTimes) return;
  return formattedDayTimes[0].timesRange;
};

/**
 * Converts and formats a price in cents to dollars
 * Will only include cents if the dollar amount isn't a round number
 *
 * @param {Integer} priceCents Price in cents, eg. 10000 or 1999
 *
 * @returns Formatted price in dollars, eg. $100 or $19.99
 */
const _formattedPrice = (priceCents) => {
  if (priceCents == 0) return 'FREE';
  const priceDollars = priceCents / 100;

  return priceDollars % 1 > 0
    ? `$${priceDollars.toFixed(2)}`
    : `$${priceDollars}`;
};

/**
 * Fetches an item unless already fetched and parses the results
 * Will store formatted data in window.vt.buyBox.offers[itemId]
 *
 * @param {String} engine Engine name to dictate API host (ie. `items-st' or `items-pr')
 * @param {String} itemId ID of the parent item
 *
 * @returns Parsed results
 */
export default async function itemFetch(engine = 'items-pr', itemId) {
  if (!itemId) return;

  // return the stored data, if present (skip fetch and parsing)
  const storedOffers = window.vt.buyBox.offers[itemId];
  if (storedOffers) return storedOffers;

  const host = engine === 'items-st' ? 'vtstaging' : 'varsitytutors';
  const fetchUrl = `https://api.${host}.com/v1/items/${itemId}?include=offers,optional_subscriptions`;

  const data = await _fetch(fetchUrl, itemId);
  const transformedResponse = _transformResponse(data);
  if (!transformedResponse) return;

  window.vt.buyBox.offers[itemId] = { offers: transformedResponse.offers };
  window.vt.buyBox.offerDates[itemId] = transformedResponse.offerDates;

  return { offers: transformedResponse.offers };
}

/**
 * Returns boolean based on if current user has a subscription
 * that can be applied to the current course offering
 *
 * @param {Object} optionalSubscription Offer optional_subscription
 * eg. { subscription_program_id: "7158d61e-06f3-4e18-8df2-ea2de23e3896",
 *       item_id: "a35e8607-a083-42b6-8db3-853c069e0be8",
 *       name: "Test Club Subscription" }
 *
 * @returns true or false
 */
const _courseIncluded = (optionalSubscription) => {
  if (optionalSubscription && optionalSubscription.subscription_program_id) {
    if (vtUnlimitedSubscription(optionalSubscription.subscription_program_id)) {
      if (userSubscriptions) {
        return userSubscriptions.includes('k-8-enrichment');
      }
    }
  }

  return false;
};

/**
 * Returns price suffix based on course offering
 *
 * @param {Object} optionalSubscription Offer optional_subscription
 * eg. { subscription_program_id: "7158d61e-06f3-4e18-8df2-ea2de23e3896",
 *       item_id: "a35e8607-a083-42b6-8db3-853c069e0be8",
 *       name: "Test Club Subscription" }
 *
 * @returns Price suffix, eg. 'total' or 'monthly'
 */
const _priceType = (optionalSubscription) => {
  if (optionalSubscription && optionalSubscription.subscription_program_id) {
    if (clubsSubscription(optionalSubscription.subscription_program_id)) {
      return 'first month';
    } else if (vtUnlimitedSubscription(optionalSubscription.subscription_program_id)) {
      return 'first month';
    } else {
      return 'monthly';
    }
  }

  return 'total';
};

/**
 * Returns heading to render in the item offer
 *
 * @param {Object} schedule Offer schedule
 * @param {Object} optionalSubscription Offer optional_subscription
 *
 * @returns Date range string, or the day-of-week and time combinations for clubs subscriptions
 */
const _offerHeading = (schedule, optionalSubscription) => {
  const startDate = _formattedDate(schedule.meetings[0].time);
  const endDate = _formattedDate(
    schedule.meetings[schedule.meetings.length - 1].time,
    schedule.session_length_minutes
  );

  if (optionalSubscription?.subscription_program_id) {
    // if it's an optional subscription, return the day-of-week and meeting time
    const weeklyDayTimes = _condensedWeeklyDayTimes(
      schedule.days_of_week_datetime
    ).map((meeting) => `${meeting.days} @ ${meeting.times}`);
    return `Meets ${weeklyDayTimes.join(', ')}`;
  } else {
    // otherwise, return the date range
    return `${startDate} - ${endDate}`;
  }
};

/**
 * Returns the class specific to the straight enrollment if it exists,
 * or the default add to cart
 *
 * @returns Class text string
 */
function singleClickClass(price) {
  return (getMeta('student-id') && price === 0) ? 'js-straight-enrollment' : 'js-add-to-cart';
}

/**
 * Returns the button text specific to the straight enrollment if it exists,
 * or the default add to cart
 *
 * @returns Button text string
 */
function singleClickText(price) {
  return (getMeta('student-id') && price === 0) ? 'Enroll Now' : 'Add to Cart';
}

/**
 * Returns the text after the price only if the price is not zero
 *
 * @returns Price text
 */
function priceText(price) {
  return price === 0 ? '' : 'total';
}

/**
 * Returns the schedule to render in the item offer
 *
 * @param {Object} schedule Offer schedule
 * @param {Object} optionalSubscription Offer optional_subscription
 *
 * @returns Array of meeting days and times, or the next session for clubs subscription
 */
const _offerSchedule = (schedule, optionalSubscription) => {
  if (optionalSubscription?.subscription_program_id) {
    // if it's an optional subscription, return the next session
    const nextSessionTime = _nextSessionTime(
      schedule.meetings,
      schedule.session_length_minutes
    );
    const formattedNextSession = _formattedDate(nextSessionTime);
    return formattedNextSession
      ? [`Next Session: ${formattedNextSession}`]
      : [];
  } else {
    // otherwise, return the meeting days and times
    return _condensedWeeklyDayTimes(schedule.days_of_week_datetime).map(
      (meeting) => `${meeting.days} @ ${meeting.times}`
    );
  }
};

/**
 * Transforms the raw API response into an array of objects that contain all the
 * offer information we need.
 *
 * @param {Object} data Data from the API response, containing offers
 */
export const _transformResponse = (data) => {
  if (!data || !data.offers) return;

  const offerDates = [];
  const requiredSubscription = data.required_subscription;

  // parse the response
  const offers = data.offers
    .map(({ offer_listing: offerListing }) => {
      if (!offerListing) return;

      const schedule = offerListing.schedule;
      const optionalSubscription = offerListing.optional_subscription;
      schedule.meetings.forEach((element) => offerDates.push(element.time));
      const formatted_price =  _formattedPrice(offerListing.price.amount);
      const price_type = offerListing.price.amount > 0 ? _priceType(optionalSubscription) : null;
      let price = formatted_price;
      if (price_type) {
        price = `${formatted_price} ${price_type}`;
      }
      const duration = offerDetails.formattedDuration(
        schedule.session_length_minutes,
        schedule.total_sessions
      );
      const formatedSessionsPerWeek = offerDetails.formattedSessionsPerWeek(schedule);

      return {
        banner_info: offerDetails.bannerInfo(offerListing, requiredSubscription),
        checkout_link: _checkoutLink(offerListing, optionalSubscription),
        duration: duration,
        end_date: offerDetails.formattedEndDate(schedule.end_date),
        enroll_text: _enrollText(offerListing.checkout_link_type, optionalSubscription),
        excluded_meeting_times: offerDetails.excludedMeetingTimes(schedule),
        exclude_offer_from_cart: requiredSubscription || optionalSubscription,
        formatted_days: _formattedDays(schedule.days_of_week_datetime),
        formatted_duration: duration,
        formatted_price: formatted_price,
        formatted_sessions_per_week: formatedSessionsPerWeek,
        formatted_total_duration: offerDetails.formattedTotalDuration(schedule.total_hours),
        formatted_times: _formattedTimes(schedule.days_of_week_datetime),
        id: offerListing.id,
        included: _courseIncluded(optionalSubscription),
        item_id: offerListing.item_id,
        item_name: data.name,
        meeting_schedule: offerDetails.formattedMeetingSchedule(schedule.meetings,
          schedule.session_length_minutes),
        next_session_time: _nextSessionTime(
          schedule.meetings,
          schedule.session_length_minutes
        ),
        offer_heading: _offerHeading(schedule, optionalSubscription),
        offer_schedule: _offerSchedule(schedule, optionalSubscription),
        price: price,
        price_in_cents: offerListing.price.amount,
        price_text: priceText(offerListing.price.amount),
        price_type: price_type,
        start_date: offerDetails.formattedStartDate(schedule.start_date),
        start_time: schedule.days_of_week_datetime[0].start_time,
        subscription_program_id: optionalSubscription?.subscription_program_id,
        week_number: weekNumber(schedule.start_date),
        single_click_text: singleClickText(offerListing.price.amount),
        single_click_class: singleClickClass(offerListing.price.amount),
        weekly_sessions: formatedSessionsPerWeek
      };
    })
    // filter out falsey values
    .filter(Boolean);

  offers.forEach(offer => {
    Object.assign(window.vt.sections, {[offer.id]: offer });
  });
  return { offers, offerDates };
};


/**
 * Fetches multiple items and returns a response similar to fetchItem.
 * Similar to the API response for a single offer, offers that have a
 * start time < today appear at the end so users see upcoming offers first.
 *
 * @param {Array} itemIds Array of parent item IDs to fetch
 *
 * @returns An object with offers, where offers is a flattened and sorted array of offers.
 */
export async function multipleItemFetch(engine = 'items-pr', itemIds) {
  if (!Array.isArray(itemIds)) return;
  const offers = [];

  await Promise.all(
    itemIds.map(async (itemId) => {
      const itemData = await itemFetch(engine, itemId);

      // if there are offers, push them to the offers array
      if (itemData && itemData.offers && itemData.offers.length > 0) {
        offers.push(itemData.offers);
      }
    })
  );

  // flatten and sort offers chronologically based on start time
  const chronOffers = offers
    .flat()
    .sort((a, b) => a.start_time.localeCompare(b.start_time));

  // move the past offers (everything before today's date) to the end of the array
  const today = new Date().toISOString().slice(0, 10);
  const nextOfferIndex = chronOffers.findIndex(
    (offer) => offer.start_time >= today
  );
  const pastOffers = chronOffers.splice(0, nextOfferIndex);
  const sortedOffers = [...chronOffers, ...pastOffers];

  return { offers: sortedOffers };
}

export function setup() {
  window.vt = window.vt || {};
  window.vt.sections = window.vt.sections || {};
  window.vt.buyBox = window.vt.buyBox || {};
  window.vt.buyBox.offers = window.vt.buyBox.offers || {};
  window.vt.buyBox.offerDates = window.vt.buyBox.offerDates || {};

  document.addEventListener('click', (event) => {
    const mediaQuery = window.matchMedia('(min-width: 460px)');
    const condensedClassTimes = document.getElementsByClassName('class-times');
    const classTimes = document.getElementsByClassName('class-times-list');
    condensedClassSummaries(condensedClassTimes, true);
    classSummaries(classTimes);

    detailsSetUp(event.target, mediaQuery, '.item-offer');
  });
}

addStartupScript(setup);
